import {
  accountSummarySchema,
  PERMISSIONS,
  roleSchema,
  staffMemberSchema,
  type AccountSummary,
  type Permission,
  type Role,
  type StaffMember,
} from '@growup/accounts-shared';
import { arenaSchema, type Arena } from '@growup/arena-shared';
import {
  Autocomplete,
  Button,
  Card,
  Checkbox,
  cx,
  Disclosure,
  Field,
  Input,
  Notice,
  Select,
  StatusBadge,
} from '@growup/ui';
import { useCallback, useEffect, useState } from 'react';
import { z } from 'zod';

import { getJson, messageOf, send } from '../api';

export const PERMISSION_LABEL: Record<Permission, string> = {
  'staff.manage': 'Gerir staff e papéis',
  'members.view': 'Ver membros adultos',
  'members.view_minors': 'Ver dados de menores',
  'members.approve': 'Aprovar pedidos de membro',
  'members.manage': 'Corrigir membros, nível e quotas',
  'members.register_youth': 'Registar jovens na Arena',
  'arena.monitor': 'Painel da sala da Arena',
  'arena.manage': 'Configurar arenas',
  'store.manage': 'Gerir a loja',
  'content.manage': 'Gerir conteúdos do site',
  'events.manage': 'Gerir eventos',
  'events.budget': 'Orçamento e faturas dos eventos',
  'streaming.manage': 'Gerir streaming',
  'streaming.padrinho': 'Padrinho/Madrinha de streamers',
  'streaming.channel': 'Canal Grow uP: grelha e pedidos',
  'giveaways.manage': 'Gerir passatempos',
  'contacts.manage': 'Mensagens de contacto',
  'contacts.direcao': 'Mensagens para a Direção',
  'partners.manage': 'Gerir parceiros',
  'social.manage': 'Gerir as redes sociais',
  'social.create': 'Influencer nas redes sociais',
  'teams.manage': 'Gerir equipas, jogos e modalidades',
  'teams.own': 'As suas equipas (Manager de equipa)',
  'tournaments.manage': 'Gerir torneios',
};

const permissionLabel = (key: string) => PERMISSION_LABEL[key as Permission] ?? key;

/** A role with any per-arena permission can be given for one arena or all. */
export const isArenaScoped = (role: Role) =>
  role.permissions.some((p) => PERMISSIONS[p as Permission]?.arenaScoped === true);

/** Areas da matriz; papeis novos, criados depois, caem em "Outros". */
export const ROLE_GROUPS: { label: string; roles: string[] }[] = [
  { label: 'Geral', roles: ['admin', 'direcao'] },
  { label: 'Arena', roles: ['monitor'] },
  { label: 'Loja', roles: ['store_manager'] },
  {
    label: 'Conteúdos',
    roles: ['content_manager', 'giveaway_manager', 'social_manager', 'influencer'],
  },
  { label: 'Streaming', roles: ['streaming_manager', 'streaming_padrinho', 'channel_programmer'] },
  {
    label: 'Eventos e equipas',
    roles: ['events_manager', 'tournament_manager', 'teams_manager', 'team_manager'],
  },
];

const ADMIN = 'admin';
const ALL = '*';

/** Uma celula da matriz: um papel, e a arena para os papeis por arena (`*` = todas). */
export type Cell = { role: Role; arenaId: string | null; label: string | null };
export const cellKey = (role: string, arenaId: string | null) => `${role}:${arenaId ?? ALL}`;

/** As colunas da matriz por area, com uma sub-coluna por arena nos papeis por arena. */
export function matrixGroups(roles: Role[], arenas: Arena[]): { label: string; cells: Cell[] }[] {
  const known = new Set(ROLE_GROUPS.flatMap((g) => g.roles));
  const groups = [
    ...ROLE_GROUPS.map((g) => ({
      label: g.label,
      roles: g.roles.flatMap((k) => roles.filter((r) => r.key === k)),
    })),
    { label: 'Outros', roles: roles.filter((r) => !known.has(r.key)) },
  ];
  return groups
    .filter((g) => g.roles.length > 0)
    .map((g) => ({
      label: g.label,
      cells: g.roles.flatMap((role): Cell[] =>
        isArenaScoped(role)
          ? [
              { role, arenaId: null, label: 'Todas' },
              ...arenas.map((a) => ({ role, arenaId: a.id, label: a.name })),
            ]
          : [{ role, arenaId: null, label: null }],
      ),
    }));
}

const keysOf = (s: StaffMember) => new Set(s.assignments.map((a) => cellKey(a.role, a.arenaId)));

type Row = { member: StaffMember; isNew: boolean };

/** Who is staff, with which roles, and giving or removing roles (`staff.manage`). */
export function StaffView({ selfId }: { selfId: string }) {
  const [staff, setStaff] = useState<StaffMember[] | null>(null);
  const [roles, setRoles] = useState<Role[]>([]);
  const [arenas, setArenas] = useState<Arena[]>([]);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    Promise.all([
      getJson('/api/identity/staff/members', z.array(staffMemberSchema)),
      getJson('/api/identity/staff/roles', z.array(roleSchema)),
    ])
      .then(([s, r]) => {
        setError(null);
        setStaff(s);
        setRoles(r);
      })
      .catch((e: unknown) => setError(messageOf(e)));
  }, []);

  useEffect(load, [load]);
  useEffect(() => {
    // Arena names for per-arena roles. Without Arena access the roles still work for all arenas.
    getJson('/api/arena/arenas', z.array(arenaSchema))
      .then(setArenas)
      .catch(() => setArenas([]));
  }, []);

  const act = async (method: string, path: string, body?: unknown) => {
    setError(null);
    try {
      await send(method, path, body);
      load();
    } catch (e) {
      setError(messageOf(e));
    }
  };

  return (
    <div className="flex flex-col gap-4">
      {error ? <Notice tone="danger">{error}</Notice> : null}
      <StaffMatrix
        staff={staff}
        roles={roles}
        arenas={arenas}
        selfId={selfId}
        onSaved={load}
        onResetMfa={(s) => {
          if (
            window.confirm(
              `Repor o segundo fator de ${s.account.login}? Terá de o configurar de novo no próximo login.`,
            )
          ) {
            void act('POST', `/api/identity/staff/accounts/${s.account.id}/mfa-reset`);
          }
        }}
      />
      <Disclosure summary="O que cada papel faz">
        <ul className="flex flex-col gap-4">
          {roles.map((r) => (
            <li key={r.id} className="flex flex-col gap-0.5">
              <p className="font-semibold">{r.name}</p>
              {r.description ? (
                <p className="text-caption text-gu-ink-muted">{r.description}</p>
              ) : null}
              <p className="text-caption text-gu-ink-muted">
                {r.permissions.map(permissionLabel).join(' · ')}
              </p>
            </li>
          ))}
        </ul>
      </Disclosure>
    </div>
  );
}

/**
 * Uma linha por pessoa e uma caixa por papel. As caixas nao gravam logo: as
 * alteracoes juntam-se e gravam-se de uma vez depois de rever o resumo.
 */
export function StaffMatrix({
  staff,
  roles,
  arenas,
  selfId,
  onSaved,
  onResetMfa,
}: {
  staff: StaffMember[] | null;
  roles: Role[];
  arenas: Arena[];
  selfId: string;
  onSaved: () => void;
  onResetMfa: (s: StaffMember) => void;
}) {
  const [added, setAdded] = useState<AccountSummary[]>([]);
  const [draft, setDraft] = useState<Record<string, Set<string>>>({});
  const [query, setQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [reviewing, setReviewing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  const groups = matrixGroups(roles, arenas);
  const roleName = (key: string) => roles.find((r) => r.key === key)?.name ?? key;
  const arenaName = (id: string) => arenas.find((a) => a.id === id)?.name ?? 'outra arena';
  const cellName = (key: string) => {
    const [role = '', arena = ALL] = key.split(':');
    const r = roles.find((x) => x.key === role);
    if (!r || !isArenaScoped(r)) return roleName(role);
    return `${r.name} · ${arena === ALL ? 'todas as arenas' : arenaName(arena)}`;
  };

  const known = staff ?? [];
  const rows: Row[] = [
    ...added
      .filter((a) => !known.some((s) => s.account.id === a.id))
      .map((account) => ({
        member: { account, assignments: [], mfaConfigured: false },
        isNew: true,
      })),
    ...known.map((member) => ({ member, isNew: false })),
  ];

  const current = (s: StaffMember) => keysOf(s);
  const valueOf = (s: StaffMember) => draft[s.account.id] ?? current(s);

  const changes = rows.flatMap(({ member }) => {
    const before = current(member);
    const after = valueOf(member);
    const plus = [...after].filter((k) => !before.has(k));
    const minus = [...before].filter((k) => !after.has(k));
    return plus.length + minus.length > 0 ? [{ member, plus, minus, after }] : [];
  });
  const changeCount = changes.reduce((n, c) => n + c.plus.length + c.minus.length, 0);

  const needle = query.trim().toLowerCase();
  const visible = rows.filter(({ member, isNew }) => {
    if (isNew) return true;
    const a = member.account;
    if (
      needle &&
      !a.login.toLowerCase().includes(needle) &&
      !(a.email ?? '').toLowerCase().includes(needle)
    )
      return false;
    return roleFilter === '' || member.assignments.some((x) => x.role === roleFilter);
  });

  const toggle = (s: StaffMember, cell: Cell) => {
    const key = cellKey(cell.role.key, cell.arenaId);
    const next = new Set(valueOf(s));
    const on = !next.has(key);
    if (cell.role.key === ADMIN) {
      const question = on
        ? `Dar Administrador geral a ${s.account.login}? Passa a poder fazer tudo, incluindo gerir o staff.`
        : `Retirar Administrador geral a ${s.account.login}?`;
      if (!window.confirm(question)) return;
    }
    if (on) next.add(key);
    else next.delete(key);
    setDone(null);
    setError(null);
    setDraft((d) => ({ ...d, [s.account.id]: next }));
  };

  /** Porque e que a caixa nao se pode mexer, ou `null` se pode. */
  const lockOf = (s: StaffMember, cell: Cell, value: Set<string>): string | null => {
    if (cell.role.key === ADMIN) {
      return s.account.id === selfId && current(s).has(cellKey(ADMIN, null))
        ? 'Não pode retirar o seu próprio papel de administrador.'
        : null;
    }
    if (value.has(cellKey(ADMIN, null))) return 'incluído';
    if (cell.arenaId !== null && value.has(cellKey(cell.role.key, null))) return 'incluído';
    return null;
  };

  const save = async () => {
    setSaving(true);
    setError(null);
    const failed: string[] = [];
    for (const c of changes) {
      try {
        await send('PUT', `/api/identity/staff/accounts/${c.member.account.id}/roles`, {
          assignments: [...c.after].map((k) => {
            const [role = '', arena = ALL] = k.split(':');
            return { role, arenaId: arena === ALL ? null : arena };
          }),
        });
      } catch (e) {
        failed.push(`${c.member.account.login}: ${messageOf(e)}`);
      }
    }
    setSaving(false);
    setReviewing(false);
    setDraft({});
    setAdded([]);
    if (failed.length > 0) setError(`Não ficou gravado: ${failed.join(' · ')}`);
    else setDone('Papéis gravados.');
    onSaved();
  };

  const box = (row: Row, cell: Cell, label: string) => {
    const s = row.member;
    const value = valueOf(s);
    const key = cellKey(cell.role.key, cell.arenaId);
    const lock = lockOf(s, cell, value);
    const changed = value.has(key) !== current(s).has(key);
    return { s, value, key, lock, changed, label };
  };

  return (
    <>
      <AddPersonForm
        staffIds={rows.map((r) => r.member.account.id)}
        onAdd={(a) => {
          setAdded((list) => [a, ...list]);
          setDone(null);
        }}
      />
      <Card headingLevel={2} title="Staff e papéis">
        <div className="flex flex-wrap items-end gap-3">
          <Field label="Procurar">
            <Input
              value={query}
              placeholder="login ou email"
              onChange={(e) => setQuery(e.target.value)}
            />
          </Field>
          <Field label="Com o papel">
            <Select value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)}>
              <option value="">Todos</option>
              {roles.map((r) => (
                <option key={r.key} value={r.key}>
                  {r.name}
                </option>
              ))}
            </Select>
          </Field>
        </div>
        {error ? <Notice tone="danger">{error}</Notice> : null}
        {done ? <Notice tone="success">{done}</Notice> : null}

        {/* Computador: a matriz, com cabecalho e 1.a coluna fixos. */}
        <div className="relative hidden max-h-[70vh] overflow-auto border border-gu-line md:block">
          <table className="border-separate border-spacing-0 text-left text-body">
            <caption className="sr-only">Papéis de cada pessoa do staff</caption>
            <thead>
              <tr>
                <th
                  rowSpan={2}
                  scope="col"
                  className="sticky left-0 top-0 z-30 border-b border-r border-gu-line bg-gu-raised px-4 py-2 font-sub text-label font-bold uppercase text-gu-ink-muted"
                >
                  Pessoa
                </th>
                {groups.map((g) => (
                  <th
                    key={g.label}
                    colSpan={g.cells.length}
                    scope="colgroup"
                    className="sticky top-0 z-20 h-9 border-b border-l border-gu-line bg-gu-raised px-2 py-1 text-center font-sub text-label font-bold uppercase text-gu-ink-muted"
                  >
                    {g.label}
                  </th>
                ))}
                <th
                  rowSpan={2}
                  scope="col"
                  className="sticky top-0 z-20 border-b border-l border-gu-line bg-gu-raised px-4 py-2 font-sub text-label font-bold uppercase text-gu-ink-muted"
                >
                  Segundo fator
                </th>
              </tr>
              <tr>
                {groups.map((g) =>
                  g.cells.map((c, i) => (
                    <th
                      key={cellKey(c.role.key, c.arenaId)}
                      scope="col"
                      title={[
                        c.role.description,
                        c.role.permissions.map(permissionLabel).join(' · '),
                      ]
                        .filter(Boolean)
                        .join('\n')}
                      className={cx(
                        'sticky top-9 z-20 w-24 min-w-24 border-b border-gu-line bg-gu-raised px-2 py-2 text-center align-bottom text-caption font-semibold',
                        i === 0 && 'border-l',
                      )}
                    >
                      {c.role.name}
                      {c.label ? (
                        <span className="block font-normal text-gu-ink-muted">{c.label}</span>
                      ) : null}
                    </th>
                  )),
                )}
              </tr>
            </thead>
            <tbody>
              {visible.map((row) => (
                <tr key={row.member.account.id}>
                  <th
                    scope="row"
                    className="sticky left-0 z-10 max-w-56 border-b border-r border-gu-line bg-gu-ground px-4 py-2 font-normal"
                  >
                    <PersonCell row={row} />
                  </th>
                  {groups.map((g) =>
                    g.cells.map((c, i) => {
                      const label = `${cellName(cellKey(c.role.key, c.arenaId))} para ${row.member.account.login}`;
                      const b = box(row, c, label);
                      return (
                        <td
                          key={b.key}
                          className={cx(
                            'border-b border-gu-line px-2 py-2 text-center',
                            i === 0 && 'border-l',
                            b.changed && 'bg-gu-accent/10',
                          )}
                        >
                          {b.lock === 'incluído' ? (
                            <span className="text-caption text-gu-ink-muted">incluído</span>
                          ) : (
                            <input
                              type="checkbox"
                              aria-label={b.label}
                              title={b.lock ?? undefined}
                              className="size-5 cursor-pointer accent-[rgb(var(--gu-accent))] disabled:cursor-not-allowed"
                              checked={b.value.has(b.key)}
                              disabled={b.lock !== null || saving}
                              onChange={() => toggle(b.s, c)}
                            />
                          )}
                        </td>
                      );
                    }),
                  )}
                  <td className="border-b border-l border-gu-line px-4 py-2">
                    <MfaCell
                      s={row.member}
                      selfId={selfId}
                      isNew={row.isNew}
                      onReset={onResetMfa}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {visible.length === 0 ? (
            <p className="p-4 text-gu-ink-muted">
              {staff ? 'Ninguém com estes filtros.' : 'A carregar…'}
            </p>
          ) : null}
        </div>

        {/* Telemovel: um cartao por pessoa, com os papeis por area. */}
        <ul className="flex flex-col gap-3 md:hidden">
          {visible.map((row) => (
            <li
              key={row.member.account.id}
              className="flex flex-col gap-3 border border-gu-line p-3"
            >
              <PersonCell row={row} />
              {groups.map((g) => (
                <fieldset key={g.label} className="flex flex-col gap-2">
                  <legend className="mb-1 font-sub text-label font-bold uppercase text-gu-ink-muted">
                    {g.label}
                  </legend>
                  {g.cells.map((c) => {
                    const name = c.label ? `${c.role.name} · ${c.label}` : c.role.name;
                    const b = box(row, c, name);
                    return (
                      <div key={b.key} className={cx('px-1', b.changed && 'bg-gu-accent/10')}>
                        <Checkbox
                          label={b.label}
                          hint={b.lock ?? undefined}
                          checked={b.lock === 'incluído' || b.value.has(b.key)}
                          disabled={b.lock !== null || saving}
                          onChange={() => toggle(b.s, c)}
                        />
                      </div>
                    );
                  })}
                </fieldset>
              ))}
              <MfaCell s={row.member} selfId={selfId} isNew={row.isNew} onReset={onResetMfa} />
            </li>
          ))}
        </ul>
      </Card>

      {changeCount > 0 ? (
        <div className="sticky bottom-0 z-40 flex flex-col gap-3 border border-gu-line bg-gu-raised p-4 shadow-lg">
          {reviewing ? (
            <>
              <p className="font-semibold">Rever antes de gravar</p>
              <ul className="flex flex-col gap-1 text-body">
                {changes.map((c) => (
                  <li key={c.member.account.id}>
                    <strong>{c.member.account.login}</strong>:{' '}
                    {[
                      ...c.plus.map((k) => `+ ${cellName(k)}`),
                      ...c.minus.map((k) => `− ${cellName(k)}`),
                    ].join(', ')}
                  </li>
                ))}
              </ul>
              <div className="flex flex-wrap gap-2">
                <Button size="sm" disabled={saving} onClick={() => void save()}>
                  {saving ? 'A gravar…' : 'Confirmar e gravar'}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={saving}
                  onClick={() => setReviewing(false)}
                >
                  Voltar
                </Button>
              </div>
            </>
          ) : (
            <div className="flex flex-wrap items-center gap-3">
              <p className="font-semibold">
                {changeCount === 1
                  ? '1 alteração por guardar'
                  : `${changeCount} alterações por guardar`}
              </p>
              <Button size="sm" onClick={() => setReviewing(true)}>
                Rever e guardar
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setDraft({});
                  setAdded([]);
                }}
              >
                Desfazer
              </Button>
            </div>
          )}
        </div>
      ) : null}
    </>
  );
}

function PersonCell({ row }: { row: Row }) {
  const a = row.member.account;
  return (
    <span className="flex min-w-0 flex-col">
      <span className="flex items-center gap-2">
        <span className="truncate font-semibold">{a.login}</span>
        {row.isNew ? <StatusBadge tone="info">nova</StatusBadge> : null}
      </span>
      {a.email ? <span className="truncate text-caption text-gu-ink-muted">{a.email}</span> : null}
    </span>
  );
}

function MfaCell({
  s,
  selfId,
  isNew,
  onReset,
}: {
  s: StaffMember;
  selfId: string;
  isNew: boolean;
  onReset: (s: StaffMember) => void;
}) {
  if (isNew) return <span className="text-caption text-gu-ink-muted">Configura no 1.º login</span>;
  return (
    <div className="flex flex-wrap items-center gap-2">
      {s.mfaConfigured ? (
        <StatusBadge tone="success">Ativo</StatusBadge>
      ) : (
        <StatusBadge tone="warning">Configura no próximo login</StatusBadge>
      )}
      {s.mfaConfigured && s.account.id !== selfId ? (
        <Button variant="ghost" size="sm" onClick={() => onReset(s)}>
          Repor
        </Button>
      ) : null}
    </div>
  );
}

function AccountSuggestion({ account }: { account: AccountSummary }) {
  return (
    <span className="flex min-w-0 flex-col">
      <span className="truncate font-semibold">{account.login}</span>
      <span className="truncate text-caption text-gu-ink-muted">
        {account.kind === 'youth' ? 'conta de jovem' : (account.email ?? 'sem email')}
      </span>
    </span>
  );
}

/** Procurar uma conta e pô-la na matriz, numa linha vazia, para marcar os papéis. */
export function AddPersonForm({
  staffIds,
  onAdd,
}: {
  staffIds: string[];
  onAdd: (account: AccountSummary) => void;
}) {
  const [login, setLogin] = useState('');
  const [error, setError] = useState<string | null>(null);

  const searchAccounts = (q: string) =>
    getJson(
      `/api/identity/staff/accounts?q=${encodeURIComponent(q)}`,
      z.array(accountSummarySchema),
    );

  return (
    <Card headingLevel={2} title="Adicionar ao staff">
      <Field label="Conta" hint="Escreva parte do login ou do email e escolha a pessoa.">
        <Autocomplete
          className="max-w-md"
          value={login}
          onChange={(v) => {
            setLogin(v);
            setError(null);
          }}
          search={searchAccounts}
          getKey={(a) => a.id}
          renderItem={(a) => <AccountSuggestion account={a} />}
          onSelect={(a) => {
            if (a.kind === 'youth') {
              setError(`${a.login} é uma conta de jovem e não pode ser staff.`);
            } else if (staffIds.includes(a.id)) {
              setError(`${a.login} já está na lista.`);
            } else {
              onAdd(a);
              setLogin('');
            }
          }}
        />
      </Field>
      {error ? <Notice tone="danger">{error}</Notice> : null}
      <p className="text-caption text-gu-ink-muted">
        A pessoa aparece no topo da tabela; marque os papéis e grave. A pessoa cria primeiro a conta
        no site. Quem recebe um papel tem de configurar o segundo fator (app autenticadora ou
        passkey) no próximo login.
      </p>
    </Card>
  );
}
